<template>
  <VModal @close="$emit('close')">
    <template #default="modalProps">
      <div
        class="rounded shadow-xs bg-gray-100 overflow-hidden mx-auto lg:max-w-7xl sm:w-full h-full flex flex-col"
      >
        <div class="flex-none px-4 sm:px-6 py-5 border-b bg-white">
          <div class="flex items-center justify-between">
            <div class="flex-1">
              <h1 class="text-sm">Sms message manager</h1>
            </div>
            <button
              type="button"
              class="rounded-md text-gray-400 hover:text-gray-800 focus:outline-none"
              @click="modalProps.close"
            >
              <!-- Heroicon name: outline/x -->
              <svg
                class="h-6 w-6"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                aria-hidden="true"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          </div>
        </div>
        <template>
          <div class="flex-1 flex divide-x min-h-0">
            <div class="flex flex-col min-h-0 w-96 flex-none divide-y bg-white">
              <div class="p-4">
                <BaseButton primary class="w-full" @click="handleShowSmsMessageForm">
                  New SMS
                </BaseButton>
              </div>
              <div class="overflow-y-auto">
                <SmsMessageList
                  ref="smsMessageList"
                  :pipeline-stage-id="pipelineStage.id"
                  :current-sms-message="currentSmsMessage"
                  @sms-message:focus="handleSmsMessageFocus"
                  @sms-message:edit="handleSmsMessageEdit"
                  @sms-message:delete="handleSmsMessageDelete"
                />
              </div>
            </div>
            <div v-if="currentSmsMessageForm" class="flex-1 p-4 sm:p-6">
              <CreateNewSmsMessageForm
                v-if="isCreateForm"
                :pipeline-stage-id="pipelineStage.id"
                @sms-message:created="handleSmsMessageCreated"
              />
              <EditSmsMessageForm
                v-else-if="isEditForm"
                :sms-message="currentSmsMessage"
                @sms-message:updated="handleSmsMessageUpdated"
              />
              <DeleteSmsMessageForm
                v-else-if="isDeleteForm"
                :sms-message="currentSmsMessage"
                @sms-message:deleted="handleSmsMessageDeleted"
              />
            </div>
            <SmsMessageManager
              v-else-if="currentSmsMessage"
              :from-options="fromOptions"
              :sms-message="currentSmsMessage"
              :pipeline-stage="pipelineStage"
              @sms-message-manager:change="handleSmsMessageManagerChange"
            />
          </div>
        </template>
      </div>
    </template>
  </VModal>
</template>

<script>
import { accountThirdPartyServicesClient } from '@/api/clients';
import SmsMessageList from './SmsMessageList.vue';
import CreateNewSmsMessageForm from './CreateNewSmsMessageForm.vue';
import EditSmsMessageForm from './EditSmsMessageForm.vue';
import DeleteSmsMessageForm from './DeleteSmsMessageForm.vue';
import SmsMessageManager from './SmsMessageManager.vue';

const FORM_CREATE = 'FORM_CREATE';
const FORM_EDIT = 'FORM_EDIT';
const FORM_DELETE = 'FORM_DELETE';

export default {
  name: 'PipelineStageSmsMessageManager',
  components: {
    SmsMessageList,
    CreateNewSmsMessageForm,
    EditSmsMessageForm,
    DeleteSmsMessageForm,
    SmsMessageManager,
  },
  props: {
    pipeline: {
      type: Object,
      required: true,
    },
    pipelineStage: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      currentSmsMessageForm: null,
      currentSmsMessage: null,
      fromOptions: [],
    };
  },
  computed: {
    isCreateForm() {
      return this.currentSmsMessageForm === FORM_CREATE;
    },
    isEditForm() {
      return this.currentSmsMessageForm === FORM_EDIT;
    },
    isDeleteForm() {
      return this.currentSmsMessageForm === FORM_DELETE;
    },
  },
  created() {
    this.fetchFromOptions();
  },
  methods: {
    fetchFromOptions() {
      return accountThirdPartyServicesClient
        .list(this.pipeline.accountId, {
          params: {
            filter: {
              channel: 'sms',
              active: true,
            },
          },
        })
        .then((response) => {
          this.fromOptions = (response?.data?.data || []).map((thirdPartyService) => ({
            value: thirdPartyService.id,
            label: thirdPartyService?.config?.name || thirdPartyService.id,
          }));

          return Promise.resolve();
        });
    },
    handleShowSmsMessageForm() {
      this.currentSmsMessageForm = FORM_CREATE;
      this.currentSmsMessage = null;
    },
    handleSmsMessageFocus(smsMessage) {
      this.currentSmsMessageForm = null;
      this.currentSmsMessage = smsMessage;
    },
    handleSmsMessageEdit(smsMessage) {
      this.currentSmsMessageForm = FORM_EDIT;
      this.currentSmsMessage = smsMessage;
    },
    handleSmsMessageDelete(smsMessage) {
      this.currentSmsMessageForm = FORM_DELETE;
      this.currentSmsMessage = smsMessage;
    },
    handleSmsMessageManagerChange() {
      this.$refs.smsMessageList.fetchData();
    },
    handleSmsMessageUpdated(data) {
      this.$refs.smsMessageList.fetchData().then((response) => {
        const smsMessage = response.data.find((item) => item.id === data.id);
        this.handleSmsMessageFocus(smsMessage || null);
        return Promise.resolve();
      });
    },
    handleSmsMessageDeleted() {
      this.$refs.smsMessageList.fetchData().then((response) => {
        const [smsMessage = null] = response.data || [];
        this.handleSmsMessageFocus(smsMessage);
        return Promise.resolve();
      });
    },
    handleSmsMessageCreated(data) {
      this.$refs.smsMessageList.fetchData().then((response) => {
        const smsMessage = response.data.find((item) => item.id === data.id);
        this.handleSmsMessageFocus(smsMessage || null);
        return Promise.resolve();
      });
    },
  },
};
</script>
